import React from "react"
import Card from "../../../../containers/Card/Card"
import CountriesField from "../../../CountriesField/CountriesField"
import CountriesFieldWithAllowUsOnlyToggle from "../../../CountriesFieldWithAllowUsOnlyToggle/CountriesFieldWithAllowUsOnlyToggle"
import ToggleSettings from "../../../ToggleSettings/ToggleSettings"
import ProhibitedCountriesIcon from "../ProhibitedCountriesIcon"
import ProhibitedCountriesSubtitle from "../ProhibitedCountriesSubtitle"
import {FormikProps} from "formik"
import {ProhibitedCountriesType} from "../../RiskSettingsUtils"
import ProhibitedCountriesTitle from "../ProhibitedCountriesTitle"

interface ProhibitedBusinessVerticalsProps {
	name: string
	value: ProhibitedCountriesType
	defaultValue?: ProhibitedCountriesType
	setFieldValue: FormikProps<ProhibitedCountriesType>["setFieldValue"]
	setFieldTouched: FormikProps<ProhibitedCountriesType>["setFieldTouched"]
}

export default function ProhibitedCountriesEditMode({
	name,
	value,
	defaultValue,
	setFieldValue,
	setFieldTouched,
}: ProhibitedBusinessVerticalsProps) {
	return (
		<div className="risk-settings-card">
			<Card
				title="Prohibited Countries"
				subtitle={<ProhibitedCountriesSubtitle />}
				className="header-with-large-gap"
				icon={<ProhibitedCountriesIcon />}
			>
				<div className="bank-risk-prohibited-countries">
					<h2>Individual Customer</h2>
					<ProhibitedCountriesTitle
						text="Physical Address"
						value={value.individualCustomer.physicalAddress}
						defaultValue={defaultValue?.individualCustomer.physicalAddress}
					/>
					<div className="field-container">
						<CountriesFieldWithAllowUsOnlyToggle
							name={`${name}.individualCustomer.physicalAddress`}
							label="Select the countries you wish to prohibit"
							value={value.individualCustomer.physicalAddress}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<ProhibitedCountriesTitle
						text="Citizenship"
						value={value.individualCustomer.citizenship}
						defaultValue={defaultValue?.individualCustomer.citizenship}
					/>
					<div className="field-container">
						<CountriesField
							name={`${name}.individualCustomer.citizenship`}
							label="Select the countries you wish to prohibit"
							value={value.individualCustomer.citizenship}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<hr className="divider" />
					<h2>Sole Proprietor Customer</h2>
					<ProhibitedCountriesTitle
						text="Physical Address"
						value={value.soleProprietorCustomer.physicalAddress}
						defaultValue={defaultValue?.soleProprietorCustomer.physicalAddress}
					/>
					<div className="field-container">
						<CountriesFieldWithAllowUsOnlyToggle
							name={`${name}.soleProprietorCustomer.physicalAddress`}
							label="Select the countries you wish to prohibit"
							value={value.soleProprietorCustomer.physicalAddress}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<ProhibitedCountriesTitle
						text="Citizenship"
						value={value.soleProprietorCustomer.citizenship}
						defaultValue={defaultValue?.soleProprietorCustomer.citizenship}
					/>
					<div className="field-container">
						<CountriesField
							name={`${name}.soleProprietorCustomer.citizenship`}
							label="Select the countries you wish to prohibit"
							value={value.soleProprietorCustomer.citizenship}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<hr className="divider" />
					<h2>Business Customer</h2>
					<ProhibitedCountriesTitle
						text="Physical Address"
						value={value.businessCustomer.physicalAddress}
						defaultValue={defaultValue?.businessCustomer.physicalAddress}
					/>
					<div className="field-container">
						<CountriesFieldWithAllowUsOnlyToggle
							name={`${name}.businessCustomer.physicalAddress`}
							label="Select the countries you wish to prohibit"
							value={value.businessCustomer.physicalAddress}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<ProhibitedCountriesTitle
						text="Countries of Operation"
						value={value.businessCustomer.countriesOfOperation}
						defaultValue={defaultValue?.businessCustomer.countriesOfOperation}
					/>
					<div className="is-us-presence-required-container">
						<ToggleSettings
							name="businessCustomer.countriesOfOperation.isUsPresenceRequired"
							title="U.S. presence required"
							checked={value.businessCustomer.countriesOfOperation.isUsPresenceRequired}
							description="This setting allows you to automatically deny any business applications that do not include U.S. in their Countries of Operation."
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setFieldValue(`${name}.businessCustomer.countriesOfOperation.isUsPresenceRequired`, e.target.checked)
							}}
						/>
					</div>
					<div className="field-container">
						<CountriesFieldWithAllowUsOnlyToggle
							name={`${name}.businessCustomer.countriesOfOperation`}
							label="Select the countries you wish to prohibit"
							value={value.businessCustomer.countriesOfOperation}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<ProhibitedCountriesTitle
						text="Beneficial Owner or Officer"
						value={value.businessCustomer.beneficialOwnerOrOfficerAddress}
						defaultValue={defaultValue?.businessCustomer.beneficialOwnerOrOfficerAddress}
					/>
					<div className="field-container">
						<CountriesFieldWithAllowUsOnlyToggle
							name={`${name}.businessCustomer.beneficialOwnerOrOfficerAddress`}
							label="Select the countries you wish to prohibit"
							value={value.businessCustomer.beneficialOwnerOrOfficerAddress}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
					<ProhibitedCountriesTitle
						text="Beneficial Owner or Officer - Citizenship"
						value={value.businessCustomer.beneficialOwnerOrOfficerCitizenship}
						defaultValue={defaultValue?.businessCustomer.beneficialOwnerOrOfficerCitizenship}
					/>
					<div className="field-container">
						<CountriesField
							name={`${name}.businessCustomer.beneficialOwnerOrOfficerCitizenship`}
							label="Select the countries you wish to prohibit"
							value={value.businessCustomer.beneficialOwnerOrOfficerCitizenship}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hideClearAllIndicator
						/>
					</div>
				</div>
			</Card>
		</div>
	)
}
